import React, { useState, useEffect, useCallback, useRef } from 'react';
import CreditsBadge from '../components/CreditsBadge';
import { Link } from 'react-router-dom';
import { useRefreshable } from '../context/RefreshContext';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import UserMenu from '../components/UserMenu';
import NavBrand from '../components/NavBrand';
import PageSpinner from '../components/PageSpinner';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import BackBar from '../components/BackBar';
import GalleryLightbox from '../components/GalleryLightbox';
import OwnerBadge from '../components/OwnerBadge';
import { SocialStats } from '../components/Social';
import { fetchLikedByUser } from '../firebase/socialService';
import { ITEM_KINDS } from '../constants/gallery';
import { currencySymbolOf } from '../constants/designer';
import './SearchPage.css';
import './Dashboard.css';
import './MyDesigners.css';
import './MyLiked.css';

// ── MY LIKED ─────────────────────────────────────────────────────────────────
// Everything you have liked — photos and 3D designs together, newest first.
// For BOTH account types: liking is not a designer's privilege, and a customer
// collecting work they admire is the more common case.
//
// ONE QUERY PER COLLECTION, on `likedBy array-contains me`. That is the payoff
// for keeping the liker list on the item document: no join, and no separate
// likes collection that would have to be kept in step.
//
// Items are shown with their counts and their author, because a page of work
// with no attribution gives you nowhere to go from something you liked.
export default function MyLiked() {
  const { t } = useT();
  const { user } = useAuth();

  const [photos, setPhotos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxAt, setLightboxAt] = useState(-1);

  // Extracted from the effect so pull-to-refresh can re-run it. `alive` moved
  // to a ref for the same reason: the guard has to survive past the one effect
  // run that created it, since a refresh can now be in flight when the page
  // unmounts.
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async () => {
    if (!user?.uid) { setLoading(false); return; }
    try {
      const { photos: ph, projects: pr } = await fetchLikedByUser(user.uid);
      if (!alive.current) return;
      const byNewest = (a, b) =>
        (b.createdAt?.toMillis?.() || b.addDateTime?.toMillis?.() || 0)
        - (a.createdAt?.toMillis?.() || a.addDateTime?.toMillis?.() || 0);
      setPhotos([...ph].sort(byNewest));
      setProjects([...pr].sort(byNewest));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { load(); }, [load]);

  // Pull to refresh re-runs the fetch in place. No `setLoading(true)` — the
  // list stays on screen and is replaced when the new data lands; blanking it
  // to a spinner under the pull indicator would be two loaders for one wait.
  useRefreshable(load);

  // Unliking from the lightbox should take the photo off this page — it is a
  // list of what you like, so an item you just unliked no longer belongs.
  function onLikeChange(id, delta) {
    if (delta < 0) setPhotos(prev => prev.filter(p => p.id !== id));
  }

  const total = photos.length + projects.length;

  return (
    <div className="dash-root">
      <header className="dash-nav">
        <NavBrand />
        <div className="dash-nav-right">
          <HeaderSearch />
          <NotificationBell />
          <CreditsBadge />
          <MessagesButton />
          {user ? <UserMenu /> : <Link to="/login" className="sp-signin">{t('nav_login')}</Link>}
        </div>
      </header>

      <BackBar width={1240} />

      <main className="dash-main md-main">
        <h1 className="sp-title">{t('ml_title')}</h1>
        <p className="md-sub">{t('ml_sub')}</p>

        {loading ? (
          <PageSpinner />
        ) : total === 0 ? (
          <p className="sp-empty">{t('ml_empty')}</p>
        ) : (
          <>
            {photos.length > 0 && (
              <section className="md-section">
                <h2 className="md-heading">{t('dg_tab_img')}</h2>
                <div className="md-photos">
                  {photos.map((p, i) => (
                    <div key={p.id} className="md-photo">
                      <button
                        type="button"
                        className="md-photo-btn"
                        onClick={() => setLightboxAt(i)}
                        title={p.title || p.description || ''}
                      >
                        <img src={p.thumbUrl} alt={p.description || ''} loading="lazy" />
                        {/* A liked PRODUCT reads like one here too — price top,
                            name bottom, the same as the feed and a shop's own
                            grid. A liked photo has neither and shows nothing. */}
                        {p.price != null && (
                          <>
                            <span className="md-price">
                              {p.price} {currencySymbolOf(p.currency)}
                            </span>
                            {p.title && <span className="md-name">{p.title}</span>}
                          </>
                        )}
                      </button>
                      <SocialStats stats={p} className="md-photo-stats" />
                      <OwnerBadge uid={p.ownerUid} className="ml-owner" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {projects.length > 0 && (
              <section className="md-section">
                <h2 className="md-heading">{t('dg_tab_3d')}</h2>
                <div className="md-photos">
                  {projects.map(pr => (
                    <div key={pr.id} className="md-photo">
                      {/* A project's own page. New tab, like everywhere else a
                          published design is opened from a list. */}
                      <a
                        className="md-photo-btn ml-project"
                        href={`/share/${pr.userId}/${pr.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="ml-project-name">{pr.projectName || t('ml_untitled')}</span>
                        <span className="ml-project-tag">3D</span>
                      </a>
                      <SocialStats stats={pr} className="md-photo-stats" />
                      <OwnerBadge uid={pr.userId} className="ml-owner" />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {lightboxAt >= 0 && photos[lightboxAt] && (
        <GalleryLightbox
          item={photos[lightboxAt]}
          kind={ITEM_KINDS.PHOTO}
          onClose={() => setLightboxAt(-1)}
          onLikeChange={onLikeChange}
        />
      )}
    </div>
  );
}
